import { usePathname, useSearchParams } from 'next/navigation'
import React, { FC, useEffect, useState } from 'react'

import { FilterFaq } from '@/features/catalog-shared/faq'
import { TitlePanel } from '@/features/catalog-shared/title-panel'
import { CatalogBanner, useCatalogBanner } from '@/features/catalog/catalog-banner'
import { CatalogFaq } from '@/features/catalog/catalog-faq'
import { useCatalogProducts } from '@/features/catalog/catalog-products'
import { CatalogSort } from '@/features/catalog/catalog-sort'
import { CategoriesGrid } from '@/features/catalog/categories-grid'
import { Filter, useFilter } from '@/features/catalog/filter'
import { GuidedFilter } from '@/features/catalog/guided-filter'
import {
	CatalogUrlProvider,
	getCatalogUrlParts,
	normalizeResolvedUrl
} from '@/features/catalog/lib/catalog-url-context'
import { SeoLinks } from '@/features/catalog/seo-links'

import { useGetCatalogCategoryQuery } from '@/entities/catalog-old/api/catalogApi'
import { useRegion } from '@/entities/regions'

import { getBannerClick, getProductImpressions } from '@/shared/integrations/yandex-metric'
import { GTMBannerData } from '@/shared/integrations/yandex-metric/types/types'
import { pushToDataLayer } from '@/shared/integrations/yandex-metric/utils/pushToDataLayer'
import { useCurrentCategory } from '@/shared/lib/hooks/useCurrentCategory'
import { sanitizeHTML } from '@/shared/lib/utils/html/sanitizeHTML'
import { isParameterCatalogUrl } from '@/shared/lib/utils/url/isParametrCatalogUrl'
import { PaginationNew } from '@/shared/ui/pagination-new'
import { ReadMoreHTML } from '@/shared/ui/ReadMoreHTML'

import { sortItems } from '../data/sort-items'

import { CatalogGrid } from './catalog-grid/catalog-grid'
import { DrawerFilter } from './drawer-filter/drawer-filter'

import cl from './catalog.module.scss'

const onBannerClick = (data: any) => {
	pushToDataLayer(getBannerClick(data, `Баннер каталога`))
}

const onBannerIntersection = (data: GTMBannerData) => {
	if (!data) return
	// pushToDataLayer(getBannersImpression([data]))
}

export const Catalog: FC = () => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const ignoreResolvedSyncRef = React.useRef(false)
	const getResolvedUrlFromRouter = (): string =>
		`${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
	const initialResolvedUrl = getResolvedUrlFromRouter()
	const [resolvedUrl, setResolvedUrl] = useState(initialResolvedUrl)
	const { pathname: resolvedPathname, search: resolvedSearch } = getCatalogUrlParts(resolvedUrl)

	const updateResolvedUrl = (nextUrl: string): void => {
		const normalizedUrl = normalizeResolvedUrl(nextUrl)
		if (resolvedUrl === normalizedUrl) {
			return
		}

		ignoreResolvedSyncRef.current = true
		setResolvedUrl(normalizedUrl)
		// Shallow URL update: keep the address bar in sync WITHOUT a Next router
		// navigation. router.replace() to the SEF /filter/ route re-runs the page
		// Server Component and remounts CatalogClient (the "page reload" on filter
		// clicks). Product/filter data already refetch via setResolvedUrl → context,
		// so history.replaceState is enough to reflect the URL. SSR/SEO for direct
		// hits and crawlers is unaffected — those still render the /filter/ route.
		window.history.replaceState(null, '', normalizedUrl)
	}

	const onPageChange = (page: number): void => {
		const query = new URLSearchParams(resolvedSearch)

		if (page <= 1) {
			query.delete('page')
		} else {
			query.set('page', String(page))
		}

		const nextUrl = `${resolvedPathname || pathname}${query.toString() ? `?${query.toString()}` : ''}`
		updateResolvedUrl(nextUrl)
	}

	const routeHasFilter = pathname.includes('/filter/')
	const routeHasFilterFromResolvedUrl = resolvedPathname.includes('/filter/')
	const effectiveRouteHasFilter = routeHasFilterFromResolvedUrl || Boolean(routeHasFilter)

	const { currentRegion } = useRegion()
	const { currentCategory, subCategories } = useCurrentCategory()
	const { data: catalogCategory } = useGetCatalogCategoryQuery(
		{ category: currentCategory.code },
		{ skip: effectiveRouteHasFilter, refetchOnFocus: false }
	)
	const isRegion = currentRegion?.REGION_CODE !== 'spb'

	const {
		onFilterChange,
		clearFilter,
		title,
		filterFaq,
		filterUrl,
		seo,
		properties,
		isLoading: isFilterLoading
	} = useFilter()
	const { getMoreProducts, products, lastPage, currentPage, loading, totalProducts } = useCatalogProducts(filterUrl)

	const { isHidden: isBannerHidden, isLoading, topBanner, middleBanner } = useCatalogBanner()

	const showFaqInFilterPage = !effectiveRouteHasFilter || !!isParameterCatalogUrl(resolvedPathname)

	const sectionSeoDescription = effectiveRouteHasFilter
		? seo?.SECTION_SEO_DESCRIPTION
		: catalogCategory?.SEO?.SECTION_SEO_DESCRIPTION

	useEffect(() => {
		const nextResolvedUrl = getResolvedUrlFromRouter()
		if (ignoreResolvedSyncRef.current) {
			ignoreResolvedSyncRef.current = false
			return
		}

		if (resolvedUrl !== nextResolvedUrl) {
			setResolvedUrl(nextResolvedUrl)
		}
	}, [pathname, searchParams, resolvedUrl])

	useEffect(() => {
		if (!products.length || !currentCategory) return

		const impressions = products.map((product, index) => ({
			product,
			location: 'Catalog Grid' as const,
			position: index + 1
		}))

		pushToDataLayer(getProductImpressions(impressions, currentCategory.title))
	}, [products, currentCategory])

	return (
		<CatalogUrlProvider
			pathname={resolvedPathname}
			search={resolvedSearch}
			resolvedUrl={resolvedUrl}
			updateResolvedUrl={updateResolvedUrl}>
			<div className={cl.root}>
				<TitlePanel title={title} productCount={totalProducts} isLoading={loading} />

				{!isBannerHidden && (
					<CatalogBanner
						data={topBanner}
						isLoading={isLoading}
						onClick={onBannerClick}
						onIntersection={onBannerIntersection}
					/>
				)}

				{/* GuidedFilter falls back to subcategory navigation chips when the category has no step
				    filter at all (e.g. «Красота и здоровье») — hence subCategories is passed down. */}
				<GuidedFilter
					className={cl.stepFilter}
					filterUrl={filterUrl}
					onFilterChange={onFilterChange}
					properties={properties}
					isFilterLoading={isFilterLoading}
					productCount={totalProducts}
					subCategories={subCategories}
				/>

				{/* Blocks replaced by GuidedFilter above. Kept mounted in the DOM (SEO relinking:
				    subcategory tiles + top seo links) but visually hidden — see cl.hiddenSeoBlocks. */}
				<div className={cl.hiddenSeoBlocks}>
					{!!subCategories?.length && (
						<CategoriesGrid currentSection={currentCategory} items={subCategories} isLoading={!subCategories?.length} />
					)}
					<SeoLinks place="top" />
				</div>

				<div className={cl.main}>
					<div className={cl.desktopOnly}>
						<Filter sectionKey={`desktop`} />
					</div>
					<div>
						<div className={cl.selectorWrap}>
							{/* todo: remove ts-ignore and fix error */}
							{/* @ts-ignore */}
							<CatalogSort sortItems={sortItems} withUrlParam />

							<div className={cl.mobileOnly}>
								<DrawerFilter productCount={totalProducts} />
							</div>
						</div>

						<CatalogGrid
							items={products}
							isLoading={loading}
							onReset={clearFilter}
							bannerItem={
								!isBannerHidden && (
									<CatalogBanner
										data={middleBanner}
										isLoading={isLoading}
										desktopHeight={150}
										onClick={onBannerClick}
										onIntersection={onBannerIntersection}
									/>
								)
							}
						/>

						{lastPage > 1 && (
							<PaginationNew
								lastPage={lastPage}
								currentPage={currentPage}
								onChange={onPageChange}
								onMoreClick={getMoreProducts}
								loading={loading}
								showMoreButton
								withUrlParam={false}
							/>
						)}
						{!isRegion && currentPage < 2 && !filterFaq?.length && showFaqInFilterPage && (
							<CatalogFaq id={currentCategory.id} title="Частые вопросы" />
						)}
						{!isRegion && !!filterFaq?.length && <FilterFaq items={filterFaq} title="Частые вопросы" />}
					</div>
				</div>

				<SeoLinks
					className={!!sectionSeoDescription && cl.catalogSeoLinks__collections}
					place="bottom"
					title="Подборки"
				/>

				{!isRegion && sectionSeoDescription && (
					<ReadMoreHTML contentShortHeight="sm" hasGradient html={sanitizeHTML(sectionSeoDescription)} />
				)}
			</div>
		</CatalogUrlProvider>
	)
}
